import { css } from '@compiled/react';

import { Box, Flex, media, Stack, xcss } from '@atlaskit/primitives';

import { LoomOnboardingButton } from '../components/loom-onboarding-button';
import { LoomOnboardingHeading } from '../components/loom-onboarding-heading';
import { LoomOnboardingText } from '../components/loom-onboarding-text';
import { RecorderCard } from '../components/recorder-card';

import type { OnboardingStepProps } from '../utilities/types';

// Responsive grid:
//  - default (mobile): single column
//  - sm+:              two equal flex columns
//  - md+:              two fixed 24rem columns
// Cards inside cap at maxWidth: 24rem and center via marginInline: auto,
// so at md+ they render at exactly 24rem, and at smaller sizes they
// stay centered within a wider column.
const gridStyles = xcss({
	display: 'grid',
	gap: 'space.500',
	gridTemplateColumns: '1fr',
	[media.above.sm]: {
		gridTemplateColumns: '1fr 1fr',
	},
	[media.above.md]: {
		gridTemplateColumns: '24rem 24rem',
		justifyContent: 'center',
	},
});

const headerBlockGap = 'space.150';
const sectionGap = 'space.500';

// Content sits 144px from the top of the page (clears the fixed Loom
// logo in the top-left) and is horizontally centered. Uses @compiled/react
// css because xcss's paddingBlockStart only accepts design tokens, and the
// exact 144px offset is required to clear the corner logo.
const pageCss = css({
	minHeight: '100vh',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	paddingBlockStart: '144px',
	paddingBlockEnd: 'var(--ds-space-400, 32px)',
	width: '100%',
});

export const DownloadOptionsStep = ({ onContinue }: OnboardingStepProps) => {
	return (
		<div css={pageCss}>
			<Stack space={sectionGap}>
				<Stack space={headerBlockGap}>
					<LoomOnboardingHeading level={2}>Choose your recorder</LoomOnboardingHeading>
					<LoomOnboardingText size="md" align="center">
						Many people start with the Chrome extension for its fast set-up.
					</LoomOnboardingText>
				</Stack>
				<Box xcss={gridStyles}>
					<RecorderCard recorder="chrome" />
					<RecorderCard recorder="desktop" />
				</Box>
				<Flex justifyContent="center">
					<LoomOnboardingButton appearance="default" onClick={onContinue}>
						Continue to Workspace
					</LoomOnboardingButton>
				</Flex>
			</Stack>
		</div>
	);
};
